import {Component, OnInit, ChangeDetectionStrategy, ViewChild, AfterViewInit, ViewContainerRef, Inject} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {untilComponentDestroyed, OnDestroyMixin} from '@w11k/ngx-componentdestroyed';
import {MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {debounceTime, filter, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {BehaviorSubject, combineLatest} from 'rxjs';

import {GpRecipientContactsDataService} from '../../../../../../services/gp/gp-recipient-contacts-data.service';
import { RecipientContactsDisplayTableComponent } from 'src/app/shared/components/recipient-contacts-display-table/recipient-contacts-display-table.component';
import { RecipientReadDto } from 'src/app/shared/models/recipient-read-dto.model';
import { RecipientContactsTableService } from 'src/app/shared/components/recipient-contacts-display-table/recipient-contacts-table.service';
import { UnitRecipientPaymentsHistoryDialogComponent } from '../../unit-recipient-payments-history/unit-recipient-payments-history-dialog/unit-recipient-payments-history-dialog.component';
import { UserService } from 'src/app/services/shared/user.service';

@Component({
  selector: 'terra-select-existing-recipient-dialog',
  templateUrl: './select-existing-recipient-dialog.component.html',
  styleUrls: ['./select-existing-recipient-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectExistingRecipientDialogComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  @ViewChild(RecipientContactsDisplayTableComponent) recipientsTable: RecipientContactsDisplayTableComponent;

  private searchOptions$ = this.recipientContactsTableService.searchOptions$.pipe(untilComponentDestroyed(this));
  private selectedTagIds$ = this.searchOptions$.pipe(map(s => s.tags));

  selectedRecipient$ = new BehaviorSubject<RecipientReadDto>(null);
  pageForm: UntypedFormGroup;

  allTags$ = this.userService.accountDetails$.pipe(
    filter(accountDetails => accountDetails.isAgent),
    switchMap(_ => this.recipientContactsDataService.getTags()),
    shareReplay(1));

  selectedTags$ = combineLatest([this.allTags$, this.selectedTagIds$]).pipe(
    map(([allTags, selectedTagIds]) => {
      return selectedTagIds.map(id => allTags[id]);
    }),
    shareReplay(1)
  );

  constructor(public dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) public data: any,
              private fb: UntypedFormBuilder,
              private recipientContactsDataService: GpRecipientContactsDataService,
              private recipientContactsTableService: RecipientContactsTableService,
              private userService:UserService,
              private dialog: MatDialog,
              private vcr: ViewContainerRef) {
    super();
    if(this.data?.paymentType !== null && this.data?.paymentType !== undefined){
      this.recipientContactsTableService.updatePaymentTypeFilter(this.data.paymentType)
    }
  }

  ngOnInit(): void {
    this.generateForm();

    
  }

  ngAfterViewInit(): void {
    this.recipientsTable.selection.changed.pipe(
      untilComponentDestroyed(this),
      map(_ => {
        const selectedRecipientArray = this.recipientsTable.selectedRecipientContacts;
        if (!selectedRecipientArray && selectedRecipientArray.length !== 1) {
          return null;
        }

        return selectedRecipientArray[0];
      }),
      tap(recipient => this.selectedRecipient$.next(recipient))
    ).subscribe();
  }

  /**
   * Generates an empty form for filters
   */
  private generateForm() {
    this.pageForm = this.fb.group({
      filter: '',
      tags: null
    });

    this.pageForm.controls.filter.valueChanges.pipe(
      untilComponentDestroyed(this),
      debounceTime(300)
    ).subscribe(term => {
      this.recipientContactsTableService.updateFilter(term);
    });

    this.pageForm.controls.tags.valueChanges.pipe(
      untilComponentDestroyed(this),
      debounceTime(300)
    ).subscribe(tags => {
      this.recipientContactsTableService.updateTagsFilter(tags);
    });

    this.recipientContactsTableService.updateFilter(null);
    this.recipientContactsTableService.updateTagsFilter(null);
  }

  /**
   * Opens payments history modal for specific recipient
   * @param recipient Recipient details
   */
  public openTransactionsHistoryModal(recipient: RecipientReadDto) {
    const dialogConfig = new MatDialogConfig<RecipientReadDto>();
    dialogConfig.disableClose = true;
    dialogConfig.viewContainerRef = this.vcr;
    dialogConfig.data = recipient;
    dialogConfig.panelClass = 'unit-recipient-payments-history'
    this.dialog.open(UnitRecipientPaymentsHistoryDialogComponent, dialogConfig).afterClosed()
      .subscribe(toRepeatPayment => {
        if (toRepeatPayment) {
          this.dialogRef.close();
        }
      });
  }
}
